import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { WEEK_DAYS } from "./DayChips";
import { TimeSelect } from "./TimeSelect";
import "./DayHourRows.css";

/* 고른 요일마다 시작~종료를 한 줄씩 — claude.ai 설정 창 "방해 금지 시간" 실측
   (docs/rail-reference.json settingsDialog.settingRow, 2026-10-19).
   이름이 왼쪽(96)에 서고 칸이 오른쪽에 붙는 가로 줄이다. 줄 12/56, 사이 16,
   가운데 "~"는 회색 14/20, 꼬리말은 12/17 회색.
   까닭: 작업자가 날마다 다른 시각에 나온다(2026-10-19 지윤). 요일 하나에 시간 하나다. */
export const DAY_LABELS: Record<string, string> = {
  일: "일요일", 월: "월요일", 화: "화요일", 수: "수요일", 목: "목요일", 금: "금요일", 토: "토요일",
};

export type DayHours = { start: string; end: string; rest?: number };

/* 하루 휴게 기본값 = 그날 근무 길이로(근로기준법: 4시간이면 30분 이상, 8시간이면 1시간 이상). */
const toMinutes = (value: string) => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
export const restFor = (start: string, end: string) => {
  const span = ((toMinutes(end) - toMinutes(start)) + 1440) % 1440 || 1440;
  return span >= 480 ? 60 : span >= 240 ? 30 : 0;
};
const REST_OPTIONS = [0, 30, 60, 90, 120];
const restLabel = (minutes: number) => minutes ? `휴게 ${minutes}분` : "휴게 없음";

/* 휴게 고르개 — 시간 칸과 같은 모양·같은 목록(rail-reference.json `quietHours.menu`, 검색칸만 없다: 짧은 목록). */
function RestSelect({ value, onChange, ariaLabel, disabled }: { value: number; onChange: (next: number) => void; ariaLabel: string; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => { if (!wrap.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { event.stopPropagation(); setOpen(false); } };
    window.addEventListener("mousedown", away); window.addEventListener("keydown", escape, true);
    return () => { window.removeEventListener("mousedown", away); window.removeEventListener("keydown", escape, true); };
  }, [open]);
  return <div className="time-select time-select--rest" ref={wrap}>
    <button type="button" className="time-select-trigger" aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} disabled={disabled}
      onClick={() => setOpen(current => !current)}><span>{restLabel(value)}</span><ChevronDown size={16} aria-hidden="true" /></button>
    {open && <div className="time-select-menu">
      <div className="time-select-list" role="listbox" aria-label={ariaLabel}>
        {REST_OPTIONS.map(minutes => <button key={minutes} type="button" role="option" aria-selected={minutes === value}
          onClick={() => { onChange(minutes); setOpen(false); }}>
          <span>{restLabel(minutes)}</span>{minutes === value && <Check size={20} aria-hidden="true" />}
        </button>)}
      </div>
    </div>}
  </div>;
}

export function DayHourRows({ days, hours, onChange, disabled }: {
  days: string[];
  hours: Record<string, DayHours>;
  onChange: (day: string, next: DayHours) => void;
  disabled?: boolean;
}) {
  const shown = WEEK_DAYS.filter(day => days.includes(day));
  if (!shown.length) return <p className="day-hour-empty">위에서 근무 요일을 고르면 요일마다 시간을 적는 줄이 생깁니다.</p>;
  return <div className="day-hour-rows">
    {shown.map(day => {
      const value = hours[day] ?? { start: "09:00", end: "18:00" };
      const rest = value.rest ?? restFor(value.start, value.end);
      /* 시각을 바꿀 때 휴게가 아직 기본값이면 새 길이의 기본값을 따라간다 — 손으로 고친 휴게는 그대로. */
      const follow = (next: DayHours) => ({ ...next, rest: rest === restFor(value.start, value.end) ? restFor(next.start, next.end) : rest });
      const overnight = value.end <= value.start;
      return <div className="day-hour-row" key={day}>
        <span className="day-hour-name">{DAY_LABELS[day] ?? day}</span>
        {/* 네이티브 time 칸은 브라우저가 제 시계 아이콘과 스피너를 그려 저쪽과 맞출 수가 없다 —
            30분 단위 목록을 직접 그린 TimeSelect 를 쓴다. 가운데 "~"는 한국어 claude.ai 실측값이다
            (영문판은 "to"). */}
        <TimeSelect value={value.start} ariaLabel={`${DAY_LABELS[day]} 근무 시작`} placeholder="시작" disabled={disabled}
          onChange={next => onChange(day, follow({ ...value, start: next }))} />
        <span className="day-hour-sep" aria-hidden="true">~</span>
        <TimeSelect value={value.end} ariaLabel={`${DAY_LABELS[day]} 근무 종료`} placeholder="종료" disabled={disabled}
          onChange={next => onChange(day, follow({ ...value, end: next }))} />
        {/* 2026-10-19 밤 지윤: 휴게는 요일마다 — 시간 칸 옆 같은 모양의 고르개. */}
        <RestSelect value={rest} ariaLabel={`${DAY_LABELS[day]} 휴게`} disabled={disabled}
          onChange={next => onChange(day, { ...value, rest: next })} />
        {/* 저쪽의 "다음 날"과 같은 자리 — 밤을 넘기는 근무일 때만 선다. */}
        <span className="day-hour-note">{overnight ? "다음 날" : ""}</span>
      </div>;
    })}
  </div>;
}
